import type { MutableRefObject } from 'react';
import { useEffect } from 'react';

type AutoSaveParams = {
	checked: boolean;
	beraReportId: string;
	gettingBeraStepKeyResult: boolean;
	stressLevelId?: string;
	frequencyId?: string;
	totalTaskDurationId?: string;
	jobElement?: string;
	stepId: string;
	stepKeyId: string;
	onAutoSave: (stepId: string, stepKeyId: string, isEmpty?: boolean) => Promise<void>;
	isFirstRender: MutableRefObject<boolean>;
	hasLoadedInitialData: MutableRefObject<boolean>;
	prevStressLevelId: MutableRefObject<string | undefined>;
	prevFrequencyId: MutableRefObject<string | undefined>;
	prevTotalTaskDurationId: MutableRefObject<string | undefined>;
	prevJobElement: MutableRefObject<string | undefined>;
	prevChecked: MutableRefObject<boolean>;
	debounceRef: MutableRefObject<NodeJS.Timeout | undefined>;
	isSaving: MutableRefObject<boolean>;
};

export function useStepKeyAutosave({
	checked,
	beraReportId,
	gettingBeraStepKeyResult,
	stressLevelId,
	frequencyId,
	totalTaskDurationId,
	jobElement,
	stepId,
	stepKeyId,
	onAutoSave,
	isFirstRender,
	hasLoadedInitialData,
	prevStressLevelId,
	prevFrequencyId,
	prevTotalTaskDurationId,
	prevJobElement,
	prevChecked,
	debounceRef,
	isSaving
}: AutoSaveParams) {
	useEffect(() => {
		function clearDebounce() {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		}

		function scheduleAutoSave(isEmpty: boolean = false) {
			debounceRef.current = setTimeout(() => {
				if (isSaving.current) return;
				isSaving.current = true;
				onAutoSave(stepId, stepKeyId, isEmpty)
					.catch(() => {})
					.finally(() => {
						isSaving.current = false;
					});
			}, 400);
		}

		if (isFirstRender.current || !hasLoadedInitialData.current) return;

		if (!beraReportId || gettingBeraStepKeyResult) return;

		if (!checked) {
			const wasChecked = prevChecked.current;
			prevChecked.current = false;
			if (!wasChecked) return;

			clearDebounce();
			prevStressLevelId.current = undefined;
			prevFrequencyId.current = undefined;
			prevTotalTaskDurationId.current = undefined;
			prevJobElement.current = undefined;
			scheduleAutoSave(true);
			return;
		}

		prevChecked.current = true;

		const hasChanged =
			prevStressLevelId.current !== stressLevelId ||
			prevFrequencyId.current !== frequencyId ||
			prevTotalTaskDurationId.current !== totalTaskDurationId ||
			prevJobElement.current !== jobElement;

		if (!hasChanged) return;

		clearDebounce();
		prevStressLevelId.current = stressLevelId;
		prevFrequencyId.current = frequencyId;
		prevTotalTaskDurationId.current = totalTaskDurationId;
		prevJobElement.current = jobElement;
		scheduleAutoSave();

		return () => {
			clearDebounce();
		};
	}, [
		checked,
		beraReportId,
		gettingBeraStepKeyResult,
		stressLevelId,
		frequencyId,
		totalTaskDurationId,
		jobElement,
		stepId,
		stepKeyId,
		onAutoSave,
		isFirstRender,
		hasLoadedInitialData,
		prevStressLevelId,
		prevFrequencyId,
		prevTotalTaskDurationId,
		prevJobElement,
		prevChecked,
		debounceRef,
		isSaving
	]);
}
